/* Core & libraries */
import React, { useState, useEffect } from "react";
import { useTheme } from "react-jss";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { useLocation } from "react-router-dom"

/* Helpers, constants, custom methods, internal libraries */
import {
  SETTINGS_ROUTE,
  SETTINGS_EMAILDOMAINS_ROUTE,
  SETTINGS_INTEGRATIONS_ROUTE,
  SETTINGS_NOTIFICATIONS_ROUTE,
  AIRLINEPROGRAMS_ROUTE,
  SETTINGS_BOOKINGFIELDS_ROUTE,
  SETTINGS_FAREACCESS_ROUTE
} from "../../constants/routes";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useIsMobile } from "../../hooks/useIsMobile.js";
import { useUserContext } from "../../contexts/userContext";
import { AIRLINES } from "../../constants/appConstants";

/* Components */
import UCSelect from "../ucselect/index.js";
import EmailDomainsTab from "./emailDomainsTab";
import IntegrationsTab from "./integrationsTab";
import NotificationsTab from "./notificationsTab";
import BookingFieldsTab from "./bookingFieldsTab";
import FareAccessTab from "./fareAccessTab";
import GeneralTabAA from "./generalTab/generalTabAA";
import GeneralTabAS from "./generalTab/generalTabAS";
import GeneralTabCHOICE from './generalTab/generalTabCHOICE';
import GeneralTabAM from './generalTab/generalTabAM';
import GeneralTabAC from './generalTab/generalTabAC';

/* Styles */
import styles, { tabsSelectOpen } from "./styles.js"
import classNames from "classnames";

const tabOptions = [
  { value: 'general', label: <FormattedMessage id="settings.tabs.general" /> },
  { value: 'emailDomains', label: <FormattedMessage id="settings.tabs.emailDomains" /> },
  // { value: 'notifications', label: <FormattedMessage id="settings.tabs.notifications" /> },
]

const SettingsTabbedNav = ({ companyMigratedChoice = false }) => {
  const location = useLocation();
  const userDetails = useUserContext();
  const userContext = useUserContext();
  const { checkPermission } = useHasPermission();
  const isMobile = useIsMobile();

  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;

  const getDefaultTab = () => {
    if (companyMigratedChoice) return "general";
    let tab = "general";

    if (location.pathname.includes(SETTINGS_ROUTE)) {
      tab = "general";
    }

    if (location.pathname.includes(SETTINGS_EMAILDOMAINS_ROUTE)) {
      tab = "emailDomains";
    }

    if (location.pathname.includes(SETTINGS_INTEGRATIONS_ROUTE)) {
      tab = "integrations";
    }

    if (location.pathname.includes(SETTINGS_NOTIFICATIONS_ROUTE)) {
      tab = "notifications";
    }

    if (location.pathname.includes(AIRLINEPROGRAMS_ROUTE)) {
      tab = "airlinePrograms";
    }

    if (location.pathname.includes(SETTINGS_BOOKINGFIELDS_ROUTE) && checkPermission("F_COMPANYSETTINGS_VIEWBOOKINGFIELDS") && providerCode === AIRLINES.AS) {
      tab = "bookingFields";
    }

    if (location.pathname.includes(SETTINGS_FAREACCESS_ROUTE) && checkPermission("F_COMPANYSETTINGS_VIEWFAREACCESS") && providerCode === AIRLINES.AS && userContext?.manageCompany ) {
      tab = "fareAccess";
    }

    return tab
  }

  const [activeTab, setActiveTab] = useState(getDefaultTab());

  useEffect(() => {
    const newTab = getDefaultTab()
    setActiveTab(newTab);
  }, [location.pathname]);


  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();

  const handleTabChange = nextTab => {
    switch (nextTab) {
      case "general":
        navigate('../' + SETTINGS_ROUTE);
        break;
      case "emailDomains":
        navigate('../' + SETTINGS_EMAILDOMAINS_ROUTE)
        break;
      case "integrations":
        navigate('../' + SETTINGS_INTEGRATIONS_ROUTE)
        break;
      case "notifications":
        navigate('../' + SETTINGS_NOTIFICATIONS_ROUTE)
        break;
      case "airlinePrograms":
        navigate('../' + AIRLINEPROGRAMS_ROUTE)
        break;
      case "bookingFields":
        navigate('../' + SETTINGS_BOOKINGFIELDS_ROUTE)
        break;
      case "fareAccess":
        navigate('../' + SETTINGS_FAREACCESS_ROUTE)
      default:
        break;
    }
  }

  const getmobileTabView = (keyTab) => {
    if (keyTab === "general") {
      return checkPermission("F_VIEW_COMPANYSETTINGS") && renderProfileTab()
    }

    if (keyTab === "emailDomains") {
      return checkPermission("F_COMPANYSETTINGS_VIEWDOMAINS") && <EmailDomainsTab />
    }

    if (keyTab === "integrations") {
      return checkPermission("F_COMPANYSETTINGS_VIEWDATASHARINGTAB") && <IntegrationsTab />
    }

    if (keyTab === "fareAccess" && checkPermission("F_COMPANYSETTINGS_VIEWFAREACCESS")) {
      return checkPermission("F_COMPANYSETTINGS_VIEWFAREACCESS") && providerCode === AIRLINES.AS && userContext?.manageCompany && <FareAccessTab />
    }

    // if(keyTab === "notifications") {
    //   return checkPermission("F_COMPANYSETTINGS_VIEWNOTIFICATIONTAB") && <NotificationsTab />
    // }

    if (keyTab === "bookingFields") {
      return checkPermission("F_COMPANYSETTINGS_VIEWBOOKINGFIELDS") && providerCode === AIRLINES.AS && <BookingFieldsTab />
    }
  }

  const renderProfileTab = () => {
    switch (providerCode) {
      case AIRLINES.AA:
        return <GeneralTabAA />;
      case AIRLINES.AS:
        return <GeneralTabAS />;
      case AIRLINES.CHOICE:
        return <GeneralTabCHOICE />;
      case AIRLINES.AM:
        return <GeneralTabAM />;
      case AIRLINES.AC:
        return <GeneralTabAC />;
    }
  }

  return (
    <div className={classes.peopleTabbedNav}>
      {isMobile ? (
        <>
          {!companyMigratedChoice && (
            <UCSelect
              styles={{ ...tabsSelectOpen }}
              defaultValue={{
                value: getDefaultTab(),
                label: <FormattedMessage id={`settings.tabs.${getDefaultTab()}`} />
              }}
              options={[
                ...tabOptions,
                ...checkPermission("F_COMPANYSETTINGS_VIEWDATASHARINGTAB") ? [{ value: 'integrations', label: <FormattedMessage id="settings.tabs.integrations" /> }] : [],
                ...checkPermission("F_COMPANYSETTINGS_VIEWBOOKINGFIELDS") && providerCode === AIRLINES.AS ? [{ value: 'bookingFields', label: <FormattedMessage id="settings.tabs.bookingFields" /> }] : [],
                ...checkPermission("F_COMPANYSETTINGS_VIEWFAREACCESS") && providerCode === AIRLINES.AS && userContext?.manageCompany ? [{ value: 'fareAccess', label: <FormattedMessage id="settings.tabs.fareAccess" /> }] : []
              ]}
              onChange={(tab) => handleTabChange(tab.value)}
            />
          )}
          {getmobileTabView(getDefaultTab())}
        </>
      ) : (
        <Tabs
          onSelect={nextTab => handleTabChange(nextTab)}
          activeKey={activeTab}
          id="settings-page-tabs"
          className={classNames(classes.tabs, companyMigratedChoice && classes.hideTabs)}
        >
          {
            checkPermission("F_VIEW_COMPANYSETTINGS") &&
            <Tab eventKey="general" title={<FormattedMessage id="settings.tabs.general" />}>
              {activeTab === "general" && renderProfileTab()}
            </Tab>
          }
          {
            checkPermission("F_COMPANYSETTINGS_VIEWDOMAINS") &&
            <Tab eventKey="emailDomains" title={<FormattedMessage id="settings.tabs.emailDomains" />}>
              {activeTab === "emailDomains" && <EmailDomainsTab />}
            </Tab>
          }
          {
            checkPermission("F_COMPANYSETTINGS_VIEWDATASHARINGTAB") &&
            <Tab eventKey="integrations" title={<FormattedMessage id="settings.tabs.integrations" />}>
              {activeTab === "integrations" && <IntegrationsTab />}
            </Tab>
          }
          {checkPermission("F_COMPANYSETTINGS_VIEWFAREACCESS") && providerCode === AIRLINES.AS && userContext?.manageCompany &&
            <Tab eventKey="fareAccess" title={<FormattedMessage id="settings.tabs.fareAccess" />}>
              {activeTab === "fareAccess" && <FareAccessTab />}
            </Tab>
          }
          {/*{*/}
          {/*  checkPermission("F_COMPANYSETTINGS_VIEWNOTIFICATIONTAB") &&*/}
          {/*    <Tab eventKey="notifications" title={<FormattedMessage id="settings.tabs.notifications" />}>*/}
          {/*      <NotificationsTab />*/}
          {/*    </Tab>*/}
          {/*}*/}
          {
            checkPermission("F_COMPANYSETTINGS_VIEWBOOKINGFIELDS") && providerCode === AIRLINES.AS &&
            <Tab eventKey="bookingFields" title={<FormattedMessage id="settings.tabs.bookingFields" />}>
              {activeTab === "bookingFields" && <BookingFieldsTab />}
            </Tab>
          }
        </Tabs>
      )}
    </div>
  )
}

export default SettingsTabbedNav;
